import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  Button,
  Col,
  Row,
} from "../../../components/Component";
import { Badge } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import moment from "moment";
import {
  getTeleconsultationStatusBadgeTone,
  getTeleconsultationStatusDisplayLabel,
} from "../../../utils/teleconsultationAdminMetrics";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";

const TeleconsultationDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    http
      .get(`/api/v1/consultations/${id}`)
      .then((res) => {
        const data = res.data?.data || res.data;
        setConsultation(data || null);
      })
      .catch((e) => {
        console.error("Failed to load consultation", e);
        setError(e.response?.data?.message || "Failed to load consultation");
        setConsultation(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const formatRef = (ref) => {
    if (ref == null) return "—";
    if (typeof ref === "object") {
      const name = [ref.first_name, ref.last_name].filter(Boolean).join(" ").trim();
      return ref.email || name || ref.name || ref._id || JSON.stringify(ref).slice(0, 30);
    }
    return String(ref);
  };

  const formatSpecialty = (s) => {
    if (s == null) return "—";
    if (typeof s === "object" && s.label) return String(s.label);
    return String(s);
  };

  const getStatusBadgeClass = (status) => getTeleconsultationStatusBadgeTone(status);

  if (loading) {
    return (
      <Content>
        <Block>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Block>
      </Content>
    );
  }

  if (error || !consultation) {
    return (
      <Content>
        <Head title="Consultation not found" />
        <Block>
          <div className="text-center p-5">
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                {error}
              </div>
            )}
            <h4>Consultation not found</h4>
            <Button color="primary" onClick={() => history.push(getPublicUrl("teleconsultations"))}>
              Back to list
            </Button>
          </div>
        </Block>
      </Content>
    );
  }

  const c = consultation;

  return (
    <>
      <TeleconsultationPageShell
        headTitle={`Consultation ${(c._id || c.id || "").slice(-8)}`}
        title="Consultation details"
        description={
          <BlockDes className="text-soft">
            ID: {(c._id || c.id || "").slice(-12)}
            {c.status && (
              <>
                {" · "}
                <Badge className={`badge-dim badge-${getStatusBadgeClass(c.status)}`}>
                  {getTeleconsultationStatusDisplayLabel(c.status)}
                </Badge>
              </>
            )}
          </BlockDes>
        }
        aside={
          <Button color="light" onClick={() => history.push(getPublicUrl("teleconsultations"))}>
            Back to list
          </Button>
        }
      >
        <Block>
          <Row>
            <Col md="6" lg="6">
              <div className="card card-bordered">
                <div className="card-inner">
                  <h6 className="overline-title mb-3">General</h6>
                  <ul className="list list-sm">
                    <li>
                      <span className="text-soft">Specialty:</span>{" "}
                      {formatSpecialty(c.specialty)}
                    </li>
                    <li>
                      <span className="text-soft">Scheduled:</span>{" "}
                      {c.scheduledAt
                        ? moment(c.scheduledAt).format("DD/MM/YYYY HH:mm")
                        : "—"}
                    </li>
                    <li>
                      <span className="text-soft">Duration (min):</span>{" "}
                      {c.durationMinutes ?? "—"}
                    </li>
                    <li>
                      <span className="text-soft">Status:</span>{" "}
                      <Badge className={`badge-dim badge-${getStatusBadgeClass(c.status)}`}>
                        {getTeleconsultationStatusDisplayLabel(c.status)}
                      </Badge>
                    </li>
                  </ul>
                </div>
              </div>
            </Col>
            <Col md="6" lg="6">
              <div className="card card-bordered">
                <div className="card-inner">
                  <h6 className="overline-title mb-3">Video room</h6>
                  <ul className="list list-sm">
                    <li>
                      <span className="text-soft">Room name:</span>{" "}
                      {c.videoRoomName || "—"}
                    </li>
                    <li>
                      <span className="text-soft">Room URL:</span>{" "}
                      {c.videoRoomUrl ? (
                        <a
                          href={c.videoRoomUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open link
                        </a>
                      ) : (
                        "—"
                      )}
                    </li>
                    <li>
                      <span className="text-soft">Started:</span>{" "}
                      {c.startedAt
                        ? moment(c.startedAt).format("DD/MM/YYYY HH:mm")
                        : "—"}
                    </li>
                    <li>
                      <span className="text-soft">Ended:</span>{" "}
                      {c.endedAt
                        ? moment(c.endedAt).format("DD/MM/YYYY HH:mm")
                        : "—"}
                    </li>
                    <li>
                      <span className="text-soft">Actual duration (min):</span>{" "}
                      {c.actualDurationMinutes ?? "—"}
                    </li>
                  </ul>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md="6" lg="6">
              <div className="card card-bordered">
                <div className="card-inner">
                  <h6 className="overline-title mb-3">Patient</h6>
                  <p className="mb-0">{formatRef(c.patient || c.client)}</p>
                </div>
              </div>
            </Col>
            <Col md="6" lg="6">
              <div className="card card-bordered">
                <div className="card-inner">
                  <h6 className="overline-title mb-3">Clinic</h6>
                  <p className="mb-0">{formatRef(c.clinic)}</p>
                </div>
              </div>
            </Col>
          </Row>

          {Array.isArray(c.connectionLogs) && c.connectionLogs.length > 0 && (
            <div className="card card-bordered mt-3">
              <div className="card-inner">
                <h6 className="overline-title mb-3">Connection logs</h6>
                <ul className="list list-sm">
                  {c.connectionLogs.map((log, idx) => (
                    <li key={idx}>
                      {typeof log === "object"
                        ? `${log.role || ""} ${log.connectedAt ? moment(log.connectedAt).format("DD/MM/YY HH:mm") : ""} ${log.disconnectedAt ? `– ${moment(log.disconnectedAt).format("DD/MM/YY HH:mm")}` : ""}`.trim() || JSON.stringify(log)
                        : String(log)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

        </Block>
      </TeleconsultationPageShell>
    </>
  );
};

export default TeleconsultationDetail;
